"use client";

import React from "react";
import { Check, X, Info } from "lucide-react";
import { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MobilitySolutionMatrix() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const features = [{
    name: "Ownership",
    rent: false,
    lease: false,
    buy: true,
    description: "Full asset ownership and equity building"
  }, {
    name: "Maintenance Included",
    rent: true,
    lease: true,
    buy: false,
    description: "Routine service and repairs covered"
  }, {
    name: "Flexible Duration",
    rent: true,
    lease: false,
    buy: false,
    description: "Ability to return or swap vehicles anytime"
  }, {
    name: "Tax Benefits",
    rent: true,
    lease: true,
    buy: true,
    description: "Deductible expenses for business use"
  }, {
    name: "Upfront Cost",
    rent: "Low",
    lease: "Medium",
    buy: "High",
    description: "Initial capital required to start",
    isText: true
  }, {
    name: "Depreciation Risk",
    rent: false,
    lease: false,
    buy: true,
    description: "Exposure to vehicle value loss over time"
  }];
  return <section id="mobility-solution-matrix" ref={ref} className={`dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Find Your Perfect Mobility Match
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Compare our flexible rental, leasing, and purchasing options to see
            which model fits your lifestyle and business goals best.
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card/50 backdrop-blur-sm shadow-2xl overflow-hidden">
          <Table className="min-w-[600px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border/50">
                <TableHead className="w-[30%] pl-6 py-6 text-lg font-semibold text-foreground bg-muted/10 sticky top-0 z-10 backdrop-blur-md">
                  Feature
                </TableHead>
                <TableHead className="w-[23%] text-center py-6 text-lg font-bold text-primary bg-muted/5 sticky top-0 z-10 backdrop-blur-md">
                  Rent
                </TableHead>
                <TableHead className="w-[23%] text-center py-6 text-lg font-bold text-foreground bg-muted/5 sticky top-0 z-10 backdrop-blur-md">
                  Lease
                </TableHead>
                <TableHead className="w-[23%] text-center py-6 text-lg font-bold text-foreground bg-muted/5 sticky top-0 z-10 backdrop-blur-md">
                  Buy
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {features.map((feature, i) => <TableRow key={feature.name} data-index={i} className="border-b border-border/40 hover:bg-muted/5 transition-colors">
                  <TableCell className="pl-6 py-5 font-medium text-foreground/90">
                    <div className="flex flex-col">
                      <span className="text-base">{feature.name}</span>
                      <span className="text-xs text-muted-foreground font-normal mt-0.5">
                        {feature.description}
                      </span>
                    </div>
                  </TableCell>

                  {}
                  <TableCell className="text-center py-5">
                    <div className="flex justify-center items-center">
                      {feature.isText ? <span className="text-sm font-semibold text-foreground">
                          {feature.rent}
                        </span> : feature.rent ? <Check className="w-6 h-6 text-primary" /> : <X className="w-6 h-6 text-muted-foreground/50" />}
                    </div>
                  </TableCell>

                  {}
                  <TableCell className="text-center py-5 bg-muted/5">
                    <div className="flex justify-center items-center">
                      {feature.isText ? <span className="text-sm font-semibold text-foreground">
                          {feature.lease}
                        </span> : feature.lease ? <Check className="w-6 h-6 text-primary" /> : <X className="w-6 h-6 text-muted-foreground/50" />}
                    </div>
                  </TableCell>

                  {}
                  <TableCell className="text-center py-5">
                    <div className="flex justify-center items-center">
                      {feature.isText ? <span className="text-sm font-semibold text-foreground">
                          {feature.buy}
                        </span> : feature.buy ? <Check className="w-6 h-6 text-primary" /> : <X className="w-6 h-6 text-muted-foreground/50" />}
                    </div>
                  </TableCell>
                </TableRow>)}
            </TableBody>
            <TableFooter className="bg-transparent border-t border-border">
              <TableRow className="hover:bg-transparent">
                <TableCell className="pl-6 py-6 font-medium text-muted-foreground">
                  Recommended for
                </TableCell>
                <TableCell className="text-center py-6 px-2">
                  <div className="flex flex-col gap-3 items-center">
                    <span className="text-xs text-muted-foreground">
                      Short-term flexibility
                    </span>
                    <Button asChild size="sm" className="w-full max-w-[140px] bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground border border-primary/20">
                      <Link to="/RentalFleetPage">View Fleet</Link>
                    </Button>
                  </div>
                </TableCell>
                <TableCell className="text-center py-6 px-2 bg-muted/5">
                  <div className="flex flex-col gap-3 items-center">
                    <span className="text-xs text-muted-foreground">
                      Business & Long-term
                    </span>
                    <Button asChild size="sm" variant="outline" className="w-full max-w-[140px]">
                      <Link to="/LeaseBusinessPage">Explore Leasing</Link>
                    </Button>
                  </div>
                </TableCell>
                <TableCell className="text-center py-6 px-2">
                  <div className="flex flex-col gap-3 items-center">
                    <span className="text-xs text-muted-foreground">
                      Permanent Ownership
                    </span>
                    <Button asChild size="sm" variant="ghost" className="w-full max-w-[140px] hover:bg-muted">
                      <Link to="/PreOwnedExcellencePage">Buy Cars</Link>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Info className="w-4 h-4" />
            <span>
              Need a custom solution?{" "}
              <Link to="/contact" className="text-primary hover:underline underline-offset-4">
                Contact our fleet specialists
              </Link>{" "}
              for a tailored proposal.
            </span>
          </p>
        </div>
      </div>
    </section>;
}